import {
  EEventContextAction,
  EEventType,
  type ICategory,
  type IProductList,
} from '@mahawi/eshop-common/dist/src/types';
import { type Dispatch } from '@reduxjs/toolkit';
import React, { RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';

import { getHead } from '../../head';
import Grid from '../components/products/grid';
import { type ICategoryState } from '../reducers/category/types';
import { eventSend } from '../reducers/event/reducer';
import { type ILanguageState } from '../reducers/language/types';
import {
  load,
  loadFeatured,
  setActiveCategory,
} from '../reducers/products/reducer';
import { type IProductsState } from '../reducers/products/types';
import { getCategoryUrl } from '../utils/category';

function TruckSvg(): React.JSX.Element {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zm-.5 1.5 1.96 2.5H17V9.5h2.5zM6 18c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm13.5-1c0 .55-.45 1-1 1s-1-.45-1-1 .45-1 1-1 1 .45 1 1z" />
    </svg>
  );
}

function LockSvg(): React.JSX.Element {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z" />
    </svg>
  );
}

function StarSvg(): React.JSX.Element {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M23 12l-2.44-2.78.34-3.68-3.61-.82-1.89-3.18L12 3 8.6 1.54 6.71 4.72l-3.61.81.34 3.68L1 12l2.44 2.78-.34 3.69 3.61.82 1.89 3.18L12 21l3.4 1.46 1.89-3.18 3.61-.82-.34-3.68L23 12zm-10 3h-2v-2h2v2zm0-4h-2V7h2v4z" />
    </svg>
  );
}

function ReturnSvg(): React.JSX.Element {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 14l-5-5 1.41-1.41L11 13.17V7h2v6.17l2.59-2.58L17 12l-5 5z" />
    </svg>
  );
}

const USP_ITEMS = [
  {
    icon: <TruckSvg />,
    titleKey: 'home.uspShippingTitle',
    descKey: 'home.uspShippingDesc',
  },
  {
    icon: <LockSvg />,
    titleKey: 'home.uspPaymentTitle',
    descKey: 'home.uspPaymentDesc',
  },
  {
    icon: <StarSvg />,
    titleKey: 'home.uspQualityTitle',
    descKey: 'home.uspQualityDesc',
  },
  {
    icon: <ReturnSvg />,
    titleKey: 'home.uspReturnTitle',
    descKey: 'home.uspReturnDesc',
  },
] as const;

function HomeContainer({
  dispatch,
  Category,
  Language,
  Products,
}: {
  dispatch: Dispatch;
  Category: ICategoryState;
  Language: ILanguageState;
  Products: IProductsState;
}): React.JSX.Element {
  useEffect((): (() => void) => {
    dispatch(
      eventSend({
        type: EEventType.PAGE_HOME,
        action: EEventContextAction.ENTER,
      }),
    );
    dispatch(
      setActiveCategory({
        category: {
          uuid: '',
        },
      }),
    );

    return (): void => {
      dispatch(
        eventSend({
          type: EEventType.PAGE_HOME,
          action: EEventContextAction.LEAVE,
        }),
      );
    };
  }, [dispatch]);

  const { t } = useTranslation();

  useEffect((): void => {
    const { title } = getHead(t, t('navigation.title.home'));
    document.title = title;
  }, [t]);

  useEffect((): void => {
    dispatch(loadFeatured());
  }, [dispatch]);

  const hasFetchedRef: RefObject<boolean> = useRef(false);
  const [selectedCategory, setSelectedCategory] = useState<
    ICategory | undefined
  >(undefined);

  useEffect((): void => {
    if (hasFetchedRef.current) return;
    const { categories } = Category;
    if (categories.length === 0) return;

    hasFetchedRef.current = true;
    const cat: ICategory =
      categories[Math.floor(Math.random() * categories.length)];
    setSelectedCategory(cat);
    dispatch(load({ category: { uuid: cat.uuid }, page: 1 }));
  }, [dispatch, Category.categories]);

  const randomProducts: IProductList[] = useMemo(
    (): IProductList[] =>
      Products.products
        .filter((p: IProductList): boolean => p.stock > 0)
        .slice(0, 8),
    [Products.products],
  );

  const randomCategoryLink: string | null = useMemo((): string | null => {
    if (!selectedCategory) return null;
    return getCategoryUrl(selectedCategory, Language.languageType.code);
  }, [selectedCategory, Language.languageType.code]);

  return (
    <>
      <section className="home-hero">
        <h1 className="home-hero--title">{t('home.welcome')}</h1>
        <p className="home-hero--desc">{t('home.welcomeDescription')}</p>
        {randomCategoryLink && (
          <Link to={randomCategoryLink} className="home-hero--cta">
            {t('home.heroCta')}
          </Link>
        )}
      </section>

      <section className="home-usps" aria-label={t('home.uspShippingTitle')}>
        {USP_ITEMS.map(({ icon, titleKey, descKey }) => (
          <div key={titleKey} className="home-usp">
            <div className="home-usp--icon" aria-hidden="true">
              {icon}
            </div>
            <h2 className="home-usp--title">{t(titleKey)}</h2>
            <p className="home-usp--desc">{t(descKey)}</p>
          </div>
        ))}
      </section>

      {Products.featured.length > 0 && (
        <section className="home-featured">
          <h2 className="home-featured--title">{t('home.featuredProducts')}</h2>
          <div className="products--grid">
            <Grid products={Products.featured} headerLevel={3} />
          </div>
        </section>
      )}

      {randomProducts.length > 0 && (
        <section className="home-random">
          <div className="products--grid">
            <Grid products={randomProducts} headerLevel={3} />
          </div>
        </section>
      )}
    </>
  );
}

const mapStateToProps = ({
  Category,
  Language,
  Products,
}: {
  Category: ICategoryState;
  Language: ILanguageState;
  Products: IProductsState;
}) => ({
  Category,
  Language,
  Products,
});

export default connect(mapStateToProps)(HomeContainer);
